import clsx from "clsx";
import { Link } from "gatsby";
import type { MouseEvent, ReactNode } from "react";

import { LogoV2Img } from "@/assets/logo";
import {
  FacebookImg,
  InstagramImg,
  TelegramImg,
  TiktokImg,
  XImg,
  YoutubeImg,
} from "@/assets/social";
import StoreButtons from "@/components/StoreButtons";
import config from "@/config";
import { scrollToSection } from "@/hooks/useSmoothScroll";

const isExchangePage = () =>
  window.location.pathname === "/" || window.location.pathname === "/exchange";

const SOCIAL_ICONS: Record<string, string> = {
  x: XImg,
  facebook: FacebookImg,
  telegram: TelegramImg,
  instagram: InstagramImg,
  tiktok: TiktokImg,
  youtube: YoutubeImg,
};

// All six social icons render per the design; platforms without a configured
// account link fall back to a plain icon.
const SOCIAL_ORDER = [
  "x",
  "facebook",
  "telegram",
  "instagram",
  "tiktok",
  "youtube",
] as const;

function FooterLink({
  to,
  section,
  children,
}: {
  to: string;
  section?: string;
  children: ReactNode;
}) {
  const handleClick = (event: MouseEvent) => {
    // Same-page navigation doesn't remount the homepage, so scroll directly
    // when already there.
    if (section && isExchangePage()) {
      event.preventDefault();
      scrollToSection(section);
    }
  };
  return (
    <Link
      to={to}
      onClick={handleClick}
      className={clsx(
        "text-[12px] leading-[16px] text-white whitespace-nowrap",
        "hover:text-brand transition-colors",
      )}
    >
      {children}
    </Link>
  );
}

function LinkColumn({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[32px] items-start">
      <h3 className="text-[16px] leading-[24px] font-bold text-white">
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function Footer() {
  const socialHrefs: Record<string, string> = {};
  for (const link of config.socialMediaLinks) {
    socialHrefs[link.platform] = link.href;
  }

  return (
    <footer className="bg-page-black border-t-2 border-stroke">
      <div
        className={clsx(
          "flex flex-col lg:flex-row justify-center",
          "gap-10 lg:gap-[120px]",
          "px-4 md:px-8 py-[100px]",
        )}
      >
        {/* Left: logo, social, store buttons */}
        <div className="flex flex-col gap-[20px] items-start">
          <img src={LogoV2Img} alt="Tuuuz" className="w-[127px] h-[32px]" />

          <div className="flex gap-[10px] h-[32px] items-center">
            {SOCIAL_ORDER.map((platform) => {
              const icon = (
                <img
                  src={SOCIAL_ICONS[platform]}
                  alt={platform}
                  className="size-[24px] shrink-0"
                />
              );
              const href = socialHrefs[platform];
              return href ? (
                <a
                  key={platform}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={clsx(
                    "flex items-center justify-center p-[4px] size-[32px] shrink-0",
                    "rounded-[100px]",
                    "hover:opacity-85 active:opacity-75 transition-opacity",
                  )}
                >
                  {icon}
                </a>
              ) : (
                <span
                  key={platform}
                  className="flex items-center justify-center p-[4px] size-[32px] shrink-0"
                >
                  {icon}
                </span>
              );
            })}
          </div>

          <div className="max-w-full overflow-x-auto">
            <StoreButtons />
          </div>
        </div>

        {/* Right: link columns */}
        <div className="flex gap-16 xl:gap-[160px] items-start">
          <LinkColumn title="Product">
            <FooterLink to="/?section=earn" section="earn">
              Earn
            </FooterLink>
            <FooterLink to="/?section=contracts" section="contracts">
              Contract
            </FooterLink>
          </LinkColumn>
          <LinkColumn title="Service">
            <FooterLink to="/?section=about-us" section="about-us">
              About Us
            </FooterLink>
            <FooterLink to="/?section=help-center" section="help-center">
              Help Center
            </FooterLink>
            <FooterLink to="/">News</FooterLink>
          </LinkColumn>
          <LinkColumn title="Support">
            <FooterLink to="/invite-friends">Invite Friends</FooterLink>
            <FooterLink to="/?section=earn" section="earn">
              How It Works
            </FooterLink>
          </LinkColumn>
        </div>
      </div>

      {/* Copyright */}
      <div className="h-[80px] flex items-center justify-center">
        <p className="text-[14px] leading-[20px] text-white text-center">
          Copyright © {new Date().getFullYear()} Tuuuz Inc. All rights
          reserved.
        </p>
      </div>
    </footer>
  );
}
